import { useCallback, useEffect, useMemo, useState } from "react";

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";

import { CountrySettingsModal } from "@/components/admin/countries/country-settings-modal";
import {
  useInvalidateLaunch,
  useLaunchSummaries,
} from "@/components/admin/countries/launch/use-launch";
import {
  COUNTRY_MODAL_TAB_IDS,
  DEFAULT_COUNTRY_MODAL_TAB,
} from "@/components/admin/countries/modal-tabs";
import type { Country } from "@/components/admin/countries/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpandableRow } from "@/components/ui/responsive-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAdminContext } from "@/lib/admin-context";
import { LEVEL_CONFIG, type CountryLevel } from "@/lib/countries";
import { getActiveLangs } from "@/lib/i18n/get-active-languages";
import { withSavingToast } from "@/lib/ui/toast";

/**
 * Admin → Countries.
 *
 * V1.1 P0 — D21 STRICT 1:1 with legacy. List + level filter + search + bulk status
 * change + click-row-opens-modal with all 11 section editors (general / features /
 * AI profile / messaging / payments / health / founding / referrals / legal /
 * competitor — first-names is embedded inside general per legacy semantics).
 *
 * Endpoints (all live):
 *   - GET   /api/admin/countries/full
 *   - PUT   /api/admin/countries                   (single: { id, [field]: value }; bulk: { ids, ...data })
 *   - POST  /api/admin/cities                       (cities import)
 *   - POST  /api/admin/translate-batch             (cities AI translate, 2x: name + adminRegion)
 *   - POST  /api/admin/country-waitlist/notify     (notify waitlist on platformStatus change)
 *   - GET   /api/admin/country-launch/summary      (🚀 launch readiness per non-hidden country)
 *
 * The open modal lives in the URL (`?open=RO&tab=launch`, replace-navigation),
 * so leaving for a page the Launch tab links to and pressing Back reopens it.
 */

const LEVEL_FILTERS = ["all", "booking", "directory", "seeding", "coming_soon", "hidden"] as const;
type LevelFilter = (typeof LEVEL_FILTERS)[number];

interface CityRow {
  id: string;
  asciiName: string;
  name: Record<string, string> | string | null;
  adminRegion: Record<string, string> | string | null;
  population: number;
  lat: number;
  lng: number;
}

interface CountriesSearch {
  level?: LevelFilter;
  q?: string;
  /** Country code whose settings modal is open. */
  open?: string;
  /** Active modal tab; absent = the default (Launch). */
  tab?: string;
}

/** "64%" or "64% · 3 blockers" — the launch readiness of a country in the list. */
function LaunchReadiness({ percent, blockers }: { percent: number; blockers: number }) {
  return (
    <>
      {percent}%
      {blockers > 0 ? (
        <>
          {" · "}
          <Plural value={blockers} one="# blocker" other="# blockers" />
        </>
      ) : null}
    </>
  );
}

function getCountryLevel(country: Country): CountryLevel {
  if (!country.platformStatus || country.platformStatus === "hidden") return "hidden";
  if (country.platformStatus === "coming_soon") return "coming_soon";
  const claimed = country.totalClaimed ?? 0;
  if (claimed >= 50) return "booking";
  if (claimed >= 1) return "directory";
  return "seeding";
}

export const Route = createFileRoute("/_authenticated/countries")({
  component: CountriesPage,
  validateSearch: (search: Record<string, unknown>): CountriesSearch => ({
    level: LEVEL_FILTERS.includes(search.level as LevelFilter)
      ? (search.level as LevelFilter)
      : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
    open:
      typeof search.open === "string" && /^[A-Za-z]{2}$/.test(search.open)
        ? search.open.toUpperCase()
        : undefined,
    tab:
      typeof search.tab === "string" && COUNTRY_MODAL_TAB_IDS.includes(search.tab)
        ? search.tab
        : undefined,
  }),
});

function CountriesPage() {
  const { t } = useLingui();
  const search = useSearch({ from: "/_authenticated/countries" });
  const navigate = useNavigate({ from: "/countries" });
  const { dbTarget } = useAdminContext();
  const filterLevel: LevelFilter = search.level ?? "all";

  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(search.q ?? "");
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>("");
  const [savingBulk, setSavingBulk] = useState(false);

  // Modal state lives in the URL (`?open=RO&tab=...`) — see setModalSearch.
  const openCode = search.open;
  const selected = useMemo(
    () => (openCode ? (countries.find((c) => c.code === openCode) ?? null) : null),
    [openCode, countries],
  );
  const modalOpen = selected !== null;
  const activeTab = search.tab ?? DEFAULT_COUNTRY_MODAL_TAB;
  const [saving, setSaving] = useState(false);

  // Cities action state
  const [importingCities, setImportingCities] = useState(false);
  const [importCitiesResult, setImportCitiesResult] = useState("");
  const [translatingCities, setTranslatingCities] = useState(false);
  const [translateCitiesResult, setTranslateCitiesResult] = useState("");
  const [deletingCities, setDeletingCities] = useState(false);
  const [deleteCitiesResult, setDeleteCitiesResult] = useState("");

  // Cities view modal state
  const [showCities, setShowCities] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citiesList, setCitiesList] = useState<CityRow[]>([]);

  // Notify-on-status-change state
  const [notifyOnStatusChange, setNotifyOnStatusChange] = useState(false);
  const [notifyResult, setNotifyResult] = useState("");

  // Active langs (V1.1 follow-up: read from /api/admin/active-languages once that
  // endpoint lands; for now seed with country's availableLanguages truncated to
  // the 8 mandatory locales).
  const langs = useMemo(() => {
    if (!selected?.availableLanguages) return ["ro", "en", "bg", "de", "fr", "hu", "it", "el"];
    return selected.availableLanguages;
  }, [selected]);

  useEffect(() => {
    setLoading(true);
    apiFetch
      .get<Country[]>("/api/admin/countries/full")
      .then(setCountries)
      .catch(() => setCountries([]))
      .finally(() => setLoading(false));
  }, [dbTarget]);

  const setModalSearch = useCallback(
    (open: string | undefined, tab: string | undefined) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          open,
          tab: tab && tab !== DEFAULT_COUNTRY_MODAL_TAB ? tab : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  // 🚀 launch readiness for the countries that are not hidden.
  const launchCodes = useMemo(
    () =>
      countries.filter((c) => c.platformStatus && c.platformStatus !== "hidden").map((c) => c.code),
    [countries],
  );
  const launchSummaries = useLaunchSummaries(launchCodes);
  const launchStepsLabel = (done: number, counted: number) =>
    t`${done} of ${counted} required steps done`;

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return countries.filter((c) => {
      if (filterLevel !== "all") {
        const lvl = getCountryLevel(c);
        if (lvl !== filterLevel) return false;
      }
      if (!q) return true;
      return (
        c.code.toLowerCase().includes(q) ||
        c.name.en?.toLowerCase().includes(q) ||
        c.name.ro?.toLowerCase().includes(q)
      );
    });
  }, [countries, filterLevel, searchTerm]);

  // Mutate the source array in-place so modal sees fresh data without remount
  // Every successful save can change the launch checklist: mark it stale so
  // the Launch tab and the 🚀 column recompute when they are next shown.
  const invalidateLaunch = useInvalidateLaunch();
  const updateCountryLocal = useCallback(
    (code: string, patch: Partial<Country>) => {
      setCountries((prev) => prev.map((c) => (c.code === code ? { ...c, ...patch } : c)));
      void invalidateLaunch();
    },
    [invalidateLaunch],
  );

  const reportSaveError = useCallback(
    (err: unknown, fallback: string) => {
      let msg = fallback;
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        msg = body?.error ?? `${fallback} (HTTP ${err.status})`;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      window.alert(`${t`Save failed`}: ${msg}`);
    },
    [t],
  );

  const onSave = useCallback(
    async (field: string, value: unknown) => {
      if (!selected) return;
      setSaving(true);
      try {
        await apiFetch.put("/api/admin/countries", { id: selected.id, [field]: value });
        updateCountryLocal(selected.code, { [field]: value } as Partial<Country>);
      } catch (err) {
        reportSaveError(err, `Could not save ${field}`);
      }
      setSaving(false);
    },
    [selected, updateCountryLocal, reportSaveError],
  );

  const onFeatureToggle = useCallback(
    async (key: string, value: boolean | number | string | string[]) => {
      if (!selected) return;
      setSaving(true);
      try {
        const newFeatures = { ...(selected.features ?? {}), [key]: value };
        await apiFetch.put("/api/admin/countries", {
          id: selected.id,
          features: newFeatures,
        });
        updateCountryLocal(selected.code, { features: newFeatures });
      } catch (err) {
        reportSaveError(err, `Could not toggle feature ${key}`);
      }
      setSaving(false);
    },
    [selected, updateCountryLocal, reportSaveError],
  );

  const onStatusChange = useCallback(
    async (status: string) => {
      if (!selected) return;
      const oldStatus = selected.platformStatus || "hidden";
      setSaving(true);
      try {
        await apiFetch.put("/api/admin/countries", {
          id: selected.id,
          platformStatus: status,
        });
        updateCountryLocal(selected.code, { platformStatus: status });
      } catch (err) {
        reportSaveError(err, `Could not update status to ${status}`);
        setSaving(false);
        return;
      }
      try {
        if (notifyOnStatusChange) {
          setNotifyResult(t`Sending notifications...`);
          try {
            const r = await apiFetch.post<{ notified: number }>(
              "/api/admin/country-waitlist/notify",
              {
                countryCode: selected.code,
                oldStatus,
                newStatus: status,
              },
            );
            setNotifyResult(`✅ ${r.notified} ${t`waitlist members notified`}`);
          } catch {
            setNotifyResult(`❌ ${t`Notify failed`}`);
          }
          setTimeout(() => setNotifyResult(""), 6000);
        }
      } catch {
        /* ignore */
      }
      setSaving(false);
    },
    [selected, notifyOnStatusChange, t, updateCountryLocal, reportSaveError],
  );

  const onImportCities = useCallback(
    async (code: string, minPopulation: number) => {
      setImportingCities(true);
      setImportCitiesResult(t`Importing cities...`);
      try {
        const r = await apiFetch.post<{ imported: number; total: number }>("/api/admin/cities", {
          countryCode: code,
          minPopulation,
        });
        setImportCitiesResult(`✅ ${r.imported} ${t`imported`} (${r.total} ${t`total`})`);
        void invalidateLaunch();
      } catch {
        setImportCitiesResult(`❌ ${t`Import failed`}`);
      }
      setImportingCities(false);
      setTimeout(() => setImportCitiesResult(""), 6000);
    },
    [t, invalidateLaunch],
  );

  const onDeleteCities = useCallback(
    async (code: string) => {
      setDeletingCities(true);
      setDeleteCitiesResult(t`Deleting cities...`);
      try {
        const r = await apiFetch.delete<{ deleted: number }>(
          `/api/admin/cities?country=${encodeURIComponent(code)}`,
        );
        setDeleteCitiesResult(`✅ ${r.deleted} ${t`deactivated`}`);
        void invalidateLaunch();
      } catch {
        setDeleteCitiesResult(`❌ ${t`Delete failed`}`);
      }
      setDeletingCities(false);
      setTimeout(() => setDeleteCitiesResult(""), 6000);
    },
    [t, invalidateLaunch],
  );

  const onTranslateCities = useCallback(
    async (code: string) => {
      setTranslatingCities(true);
      setTranslateCitiesResult(t`Translating city names...`);
      try {
        const targetLangs = await getActiveLangs();
        const nameRes = await apiFetch
          .post<{ translated?: number }>("/api/admin/translate-batch", {
            entity: "city",
            field: "name",
            targetLangs,
            sourceLang: "en",
            countryCode: code,
          })
          .catch(() => null);

        setTranslateCitiesResult(t`Translating admin regions...`);
        const adminRes = await apiFetch
          .post<{ translated?: number }>("/api/admin/translate-batch", {
            entity: "city",
            field: "adminRegion",
            targetLangs,
            sourceLang: "en",
            countryCode: code,
          })
          .catch(() => null);

        setTranslateCitiesResult(
          `✅ ${nameRes?.translated ?? 0} ${t`names`} + ${adminRes?.translated ?? 0} ${t`regions`}`,
        );
        void invalidateLaunch();
      } catch {
        setTranslateCitiesResult(`❌ ${t`Translate failed`}`);
      }
      setTranslatingCities(false);
      setTimeout(() => setTranslateCitiesResult(""), 6000);
    },
    [t, invalidateLaunch],
  );

  const onViewCities = useCallback(async () => {
    if (!selected) return;
    setShowCities(true);
    setLoadingCities(true);
    try {
      const rows = await apiFetch.get<CityRow[]>(
        `/api/admin/cities?country=${encodeURIComponent(selected.code)}`,
      );
      setCitiesList(rows);
    } catch {
      setCitiesList([]);
    }
    setLoadingCities(false);
  }, [selected]);

  const bulkApply = useCallback(
    async <K extends keyof Country>(
      field: K,
      value: Country[K],
      labels: { saving: string; saved: string; error: string },
      onSuccess?: () => void,
    ) => {
      if (checked.size === 0) return;
      const ids = countries.filter((c) => checked.has(c.code)).map((c) => c.id);
      if (ids.length === 0) return;
      setSavingBulk(true);
      try {
        await withSavingToast(
          () => apiFetch.put("/api/admin/countries", { ids, [field]: value }),
          labels,
        );
        setCountries((prev) =>
          prev.map((c) => (checked.has(c.code) ? { ...c, [field]: value } : c)),
        );
        onSuccess?.();
      } catch {
        /* toast already shown */
      }
      setSavingBulk(false);
    },
    [checked, countries],
  );

  const updateBulkStatus = async () => {
    if (!bulkStatus) return;
    await bulkApply(
      "platformStatus",
      bulkStatus,
      {
        saving: t`Updating status…`,
        saved: t`${checked.size} countries updated`,
        error: t`Could not update status`,
      },
      () => {
        setChecked(new Set());
        setBulkStatus("");
      },
    );
  };

  const bulkSetWaitlist = (value: boolean) =>
    bulkApply("waitlistEnabled", value, {
      saving: t`Updating waitlist…`,
      saved: value
        ? t`Waitlist enabled on ${checked.size} countries`
        : t`Waitlist disabled on ${checked.size} countries`,
      error: t`Could not update waitlist`,
    });

  const bulkSetReferral = (value: boolean) =>
    bulkApply("referralEnabled", value, {
      saving: t`Updating referral…`,
      saved: value
        ? t`Referral enabled on ${checked.size} countries`
        : t`Referral disabled on ${checked.size} countries`,
      error: t`Could not update referral`,
    });

  const toggleCheck = (code: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const visibleCodes = useMemo(() => filtered.map((c) => c.code), [filtered]);
  const allVisibleChecked =
    visibleCodes.length > 0 && visibleCodes.every((code) => checked.has(code));
  const someVisibleChecked = !allVisibleChecked && visibleCodes.some((code) => checked.has(code));

  const toggleAllVisible = () => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) {
        for (const code of visibleCodes) next.delete(code);
      } else {
        for (const code of visibleCodes) next.add(code);
      }
      return next;
    });
  };

  const openCountry = (c: Country) => {
    setModalSearch(c.code, DEFAULT_COUNTRY_MODAL_TAB);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          <Trans>Countries</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>{countries.length} countries · click a row to open the section editor</Trans>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t`Search code or name...`}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            navigate({ search: { level: search.level, q: e.target.value || undefined } });
          }}
          className="w-56"
        />
        <div className="flex flex-wrap gap-1.5">
          {LEVEL_FILTERS.map((lvl) => (
            <button
              key={lvl}
              onClick={() =>
                navigate({ search: { level: lvl === "all" ? undefined : lvl, q: search.q } })
              }
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                filterLevel === lvl
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {lvl === "all" ? t`All` : (LEVEL_CONFIG[lvl as CountryLevel]?.label ?? lvl)}
            </button>
          ))}
        </div>
      </div>

      {checked.size > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-3">
            <span className="text-sm font-medium">
              <Trans>{checked.size} selected</Trans>
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="border-border bg-background rounded-md border px-2 py-1.5 text-xs"
              >
                <option value="">{t`Set status...`}</option>
                <option value="hidden">hidden</option>
                <option value="coming_soon">coming_soon</option>
                <option value="open">open</option>
              </select>
              <Button size="sm" onClick={updateBulkStatus} disabled={!bulkStatus || savingBulk}>
                {savingBulk ? <Trans>Applying...</Trans> : <Trans>Apply status</Trans>}
              </Button>
            </div>

            <div className="bg-border h-6 w-px" aria-hidden />

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground text-xs font-medium">
                <Trans>Waitlist</Trans>
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkSetWaitlist(true)}
                disabled={savingBulk}
              >
                <Trans>On</Trans>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkSetWaitlist(false)}
                disabled={savingBulk}
              >
                <Trans>Off</Trans>
              </Button>
            </div>

            <div className="bg-border h-6 w-px" aria-hidden />

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground text-xs font-medium">
                <Trans>Referral</Trans>
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkSetReferral(true)}
                disabled={savingBulk}
              >
                <Trans>On</Trans>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => bulkSetReferral(false)}
                disabled={savingBulk}
              >
                <Trans>Off</Trans>
              </Button>
            </div>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => setChecked(new Set())}
              className="ml-auto"
            >
              <Trans>Clear</Trans>
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="border-border/50 overflow-hidden rounded-xl border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-border/50 bg-muted/30 border-b">
              <th className="w-10 px-2 py-3 text-center">
                <input
                  type="checkbox"
                  aria-label={t`Select all visible countries`}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleChecked;
                  }}
                  checked={allVisibleChecked}
                  onChange={toggleAllVisible}
                  disabled={loading || filtered.length === 0}
                  className="h-4 w-4 cursor-pointer"
                />
              </th>
              <th className="px-3 py-3 text-left font-semibold">
                <Trans>Country</Trans>
              </th>
              <th className="hidden px-3 py-3 text-left font-semibold sm:table-cell">
                <Trans>Status</Trans>
              </th>
              <th className="hidden px-3 py-3 text-left font-semibold sm:table-cell">
                <Trans>Launch</Trans>
              </th>
              <th className="hidden px-3 py-3 text-left font-semibold md:table-cell">
                <Trans>Level</Trans>
              </th>
              <th className="hidden px-3 py-3 text-right font-semibold md:table-cell">
                <Trans>Entities</Trans>
              </th>
              <th className="hidden px-3 py-3 text-right font-semibold lg:table-cell">
                <Trans>Waitlist</Trans>
              </th>
              <th className="hidden px-3 py-3 text-right font-semibold lg:table-cell">
                <Trans>Claimed</Trans>
              </th>
              <th className="px-3 py-3 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-muted-foreground p-8 text-center text-sm">
                  <Trans>Loading...</Trans>
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-muted-foreground p-8 text-center text-sm">
                  <Trans>No countries match filters</Trans>
                </td>
              </tr>
            ) : (
              filtered.map((c) => {
                const level = getCountryLevel(c);
                const cfg = LEVEL_CONFIG[level];
                const launch = launchSummaries.data?.[c.code];
                const launchLabel = launch ? (
                  <LaunchReadiness percent={launch.percent} blockers={launch.openBlockers} />
                ) : (
                  "—"
                );
                return (
                  <ExpandableRow
                    key={c.code}
                    className="border-border/30 hover:bg-muted/30 cursor-pointer border-b last:border-0"
                    mobileDetails={[
                      { label: t`Status`, value: c.platformStatus },
                      { label: t`Launch`, value: launchLabel },
                      { label: t`Level`, value: cfg.label },
                      { label: t`Providers`, value: c.totalProviders },
                      { label: t`Facilities`, value: c.totalFacilities },
                      { label: t`Businesses`, value: c.totalBusinesses },
                      { label: t`Claimed`, value: c.totalClaimed },
                      { label: t`Waitlist · User`, value: c.waitlist?.user ?? 0 },
                      { label: t`Waitlist · Provider`, value: c.waitlist?.provider ?? 0 },
                      { label: t`Waitlist · Business`, value: c.waitlist?.clinic ?? 0 },
                      {
                        label: t`Waitlist · Collaborator`,
                        value: c.waitlist?.collaborator ?? 0,
                      },
                    ]}
                  >
                    <td className="px-2 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={checked.has(c.code)}
                        onChange={() => toggleCheck(c.code)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-3 py-3" onClick={() => openCountry(c)}>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{c.flagEmoji ?? "🏳️"}</span>
                        <div>
                          <div className="font-semibold">{c.name?.en || c.name?.ro || c.code}</div>
                          <div className="text-muted-foreground font-mono text-[0.625rem]">
                            {c.code}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-3 py-3 sm:table-cell" onClick={() => openCountry(c)}>
                      <Badge variant="secondary" className="text-[0.625rem]">
                        {c.platformStatus}
                      </Badge>
                    </td>
                    <td className="hidden px-3 py-3 sm:table-cell" onClick={() => openCountry(c)}>
                      {c.platformStatus === "hidden" ? (
                        <span className="text-muted-foreground text-xs">—</span>
                      ) : launch ? (
                        <Badge
                          variant={
                            launch.openBlockers > 0
                              ? "destructive"
                              : launch.percent >= 100
                                ? "success"
                                : "warning"
                          }
                          className="tabular-nums"
                          title={launchStepsLabel(launch.done, launch.counted)}
                        >
                          🚀 {launchLabel}
                        </Badge>
                      ) : launchSummaries.isPending ? (
                        <Skeleton className="h-5 w-16 rounded-full" />
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="hidden px-3 py-3 md:table-cell" onClick={() => openCountry(c)}>
                      <Badge variant="outline" className={`text-[0.625rem] ${cfg.color}`}>
                        {cfg.label}
                      </Badge>
                    </td>
                    <td className="hidden px-3 py-3 md:table-cell" onClick={() => openCountry(c)}>
                      <div className="space-y-0.5 text-xs leading-tight">
                        <div className="text-muted-foreground flex items-center justify-between gap-3">
                          <span>
                            <Trans>Providers</Trans>
                          </span>
                          <span className="text-foreground font-mono tabular-nums">
                            {c.totalProviders}
                          </span>
                        </div>
                        <div className="text-muted-foreground flex items-center justify-between gap-3">
                          <span>
                            <Trans>Facilities</Trans>
                          </span>
                          <span className="text-foreground font-mono tabular-nums">
                            {c.totalFacilities}
                          </span>
                        </div>
                        <div className="text-muted-foreground flex items-center justify-between gap-3">
                          <span>
                            <Trans>Businesses</Trans>
                          </span>
                          <span className="text-foreground font-mono tabular-nums">
                            {c.totalBusinesses}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-3 py-3 lg:table-cell" onClick={() => openCountry(c)}>
                      <div className="space-y-0.5 text-xs leading-tight">
                        <div className="text-muted-foreground flex items-center justify-between gap-3">
                          <span>
                            <Trans>User</Trans>
                          </span>
                          <span className="text-foreground font-mono tabular-nums">
                            {c.waitlist?.user ?? 0}
                          </span>
                        </div>
                        <div className="text-muted-foreground flex items-center justify-between gap-3">
                          <span>
                            <Trans>Provider</Trans>
                          </span>
                          <span className="text-foreground font-mono tabular-nums">
                            {c.waitlist?.provider ?? 0}
                          </span>
                        </div>
                        <div className="text-muted-foreground flex items-center justify-between gap-3">
                          <span>
                            <Trans>Business</Trans>
                          </span>
                          <span className="text-foreground font-mono tabular-nums">
                            {c.waitlist?.clinic ?? 0}
                          </span>
                        </div>
                        <div className="text-muted-foreground flex items-center justify-between gap-3">
                          <span>
                            <Trans>Collaborator</Trans>
                          </span>
                          <span className="text-foreground font-mono tabular-nums">
                            {c.waitlist?.collaborator ?? 0}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td
                      className="hidden px-3 py-3 text-right lg:table-cell"
                      onClick={() => openCountry(c)}
                    >
                      {c.totalClaimed}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openCountry(c);
                        }}
                      >
                        <Trans>Edit</Trans>
                      </Button>
                    </td>
                  </ExpandableRow>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <CountrySettingsModal
          open={modalOpen}
          onOpenChange={(open) => {
            if (!open) {
              setModalSearch(undefined, undefined);
              setNotifyOnStatusChange(false);
              setNotifyResult("");
              setImportCitiesResult("");
              setTranslateCitiesResult("");
              setDeleteCitiesResult("");
            }
          }}
          country={selected}
          filteredCountries={filtered}
          activeTab={activeTab}
          onTabChange={(tab) => setModalSearch(selected.code, tab)}
          onSelectCountry={(c) => setModalSearch(c.code, activeTab)}
          saving={saving}
          onSave={onSave}
          onFeatureToggle={onFeatureToggle}
          onStatusChange={onStatusChange}
          langs={langs}
          onImportCities={onImportCities}
          onTranslateCities={onTranslateCities}
          onViewCities={onViewCities}
          onDeleteCities={onDeleteCities}
          importingCities={importingCities}
          importCitiesResult={importCitiesResult}
          translatingCities={translatingCities}
          translateCitiesResult={translateCitiesResult}
          deletingCities={deletingCities}
          deleteCitiesResult={deleteCitiesResult}
          notifyOnStatusChange={notifyOnStatusChange}
          setNotifyOnStatusChange={setNotifyOnStatusChange}
          notifyResult={notifyResult}
        />
      )}

      <Dialog open={showCities} onOpenChange={setShowCities}>
        <DialogContent className="flex max-h-[85vh] w-[95vw] max-w-3xl flex-col gap-3 overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              <Trans>Cities</Trans>
              {selected ? ` ${selected.name.en || selected.name.ro || selected.code}` : ""}
              {!loadingCities && ` (${citiesList.length})`}
            </DialogTitle>
          </DialogHeader>
          <div className="-mx-1 flex-1 overflow-x-auto overflow-y-auto">
            {loadingCities ? (
              <div className="text-muted-foreground py-8 text-center text-sm">
                <Trans>Loading cities...</Trans>
              </div>
            ) : citiesList.length === 0 ? (
              <div className="text-muted-foreground py-8 text-center text-sm">
                <Trans>No cities found.</Trans>
              </div>
            ) : (
              <Table className="text-xs">
                <TableHeader className="bg-muted/50 sticky top-0">
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>
                      <Trans>Name</Trans> ({(selected?.primaryLanguage || "ro").toUpperCase()})
                    </TableHead>
                    <TableHead className="hidden sm:table-cell">
                      <Trans>Name (EN)</Trans>
                    </TableHead>
                    <TableHead className="hidden sm:table-cell">
                      <Trans>Region</Trans> ({(selected?.primaryLanguage || "ro").toUpperCase()})
                    </TableHead>
                    <TableHead className="hidden md:table-cell">
                      <Trans>Region (EN)</Trans>
                    </TableHead>
                    <TableHead className="hidden text-right md:table-cell">
                      <Trans>Population</Trans>
                    </TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Lat</TableHead>
                    <TableHead className="hidden text-right lg:table-cell">Lng</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {citiesList.map((c, i) => {
                    const nameObj =
                      typeof c.name === "string"
                        ? (() => {
                            try {
                              return JSON.parse(c.name) as Record<string, string>;
                            } catch {
                              return {} as Record<string, string>;
                            }
                          })()
                        : (c.name ?? {});
                    const adminObj =
                      typeof c.adminRegion === "string"
                        ? (() => {
                            try {
                              return JSON.parse(c.adminRegion) as Record<string, string>;
                            } catch {
                              return null;
                            }
                          })()
                        : c.adminRegion;
                    const lang = selected?.primaryLanguage || "ro";
                    const nativeName = nameObj?.[lang] || nameObj?.en || c.asciiName;
                    const enName = nameObj?.en || c.asciiName;
                    const regionNative = adminObj
                      ? adminObj[lang] ||
                        adminObj.en ||
                        Object.values(adminObj).find(Boolean) ||
                        "—"
                      : "—";
                    const regionEn = adminObj?.en || "—";

                    return (
                      <ExpandableRow
                        key={c.id}
                        mobileDetails={[
                          { label: <Trans>Name (EN)</Trans>, value: enName },
                          {
                            label: (
                              <>
                                <Trans>Region</Trans> (
                                {(selected?.primaryLanguage || "ro").toUpperCase()})
                              </>
                            ),
                            value: regionNative,
                          },
                          { label: <Trans>Region (EN)</Trans>, value: regionEn },
                          {
                            label: <Trans>Population</Trans>,
                            value: c.population.toLocaleString(),
                          },
                          { label: "Lat", value: c.lat.toFixed(4) },
                          { label: "Lng", value: c.lng.toFixed(4) },
                        ]}
                      >
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-medium">{nativeName}</TableCell>
                        <TableCell className="text-muted-foreground hidden sm:table-cell">
                          {enName}
                        </TableCell>
                        <TableCell className="hidden font-medium sm:table-cell">
                          {regionNative}
                        </TableCell>
                        <TableCell className="text-muted-foreground hidden md:table-cell">
                          {regionEn}
                        </TableCell>
                        <TableCell className="hidden text-right font-mono md:table-cell">
                          {c.population.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-muted-foreground hidden text-right font-mono text-[0.625rem] lg:table-cell">
                          {c.lat.toFixed(4)}
                        </TableCell>
                        <TableCell className="text-muted-foreground hidden text-right font-mono text-[0.625rem] lg:table-cell">
                          {c.lng.toFixed(4)}
                        </TableCell>
                      </ExpandableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
